import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Truck, X, Pencil, Trash2, Gauge, CheckCircle, Wrench, 
  AlertOctagon, Clock, User, Calendar, Camera, Image as ImageIcon,
  ClipboardList, StickyNote
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLightbox } from "@/context/LightboxContext";
import { StatusDot } from "@/components/layout/StatsCard";
import { VehicleFormModal } from "./VehicleFormModal";
import { API, SERVICE_TYPE_CONFIG } from "@/utils/constants";
import { getOutOfServiceDuration } from "@/utils/helpers";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

export const VehicleDetailModal = ({ open, onClose, vehicle, onUpdate, serviceIntervals, defaultTab = "details", currentUser }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [completedRequests, setCompletedRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    mileage_at_service: "",
    hours_at_service: "",
    notes: "",
    performed_by: "Fleet Admin",
  });
  const [serviceImages, setServiceImages] = useState([]);
  const [outOfServiceReason, setOutOfServiceReason] = useState("");
  const [outOfServiceCategory, setOutOfServiceCategory] = useState("");
  const [editingReason, setEditingReason] = useState(false);
  const [showOosDialog, setShowOosDialog] = useState(false);
  const { setLightboxImage } = useLightbox();
  
  // Out of Service Categories
  const oosCategories = [
    { value: "planned_maintenance", label: "Planned Maintenance" },
    { value: "body_shop", label: "Body Shop" },
    { value: "safety_hold", label: "Safety Hold" },
    { value: "breakdown", label: "Breakdown" },
    { value: "repair", label: "Repair" },
    { value: "administrative_hold", label: "Administrative Hold" },
    { value: "other", label: "Other" },
  ];
  
  // Permission checks
  const userRole = currentUser?.role;
  const canEdit = hasPermission(userRole, PERMISSIONS.VEHICLE_DETAIL.EDIT);
  const canDelete = hasPermission(userRole, PERMISSIONS.VEHICLE_DETAIL.DELETE);
  const canMarkOOS = hasPermission(userRole, PERMISSIONS.VEHICLE_DETAIL.MARK_OOS);
  const canReturnToService = hasPermission(userRole, PERMISSIONS.VEHICLE_DETAIL.RETURN_TO_SERVICE);
  const canRecordService = hasPermission(userRole, PERMISSIONS.VEHICLE_DETAIL.RECORD_SERVICE);

  const fetchServiceHistory = useCallback(async () => {
    if (!vehicle) return;
    try {
      const [historyRes, requestsRes] = await Promise.all([
        axios.get(`${API}/service-records?vehicle_id=${vehicle.id}`),
        axios.get(`${API}/repair-requests`)
      ]);
      // Safely sort service history, handling invalid dates
      const sortedHistory = (historyRes.data || []).sort((a, b) => {
        const dateA = a.service_date ? new Date(a.service_date) : new Date(0);
        const dateB = b.service_date ? new Date(b.service_date) : new Date(0);
        // Handle Invalid Date by treating them as epoch
        const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
        const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
        return timeB - timeA;
      });
      setServiceHistory(sortedHistory);
      // Filter completed requests for this vehicle (fulfilled or archived)
      const vehicleRequests = (requestsRes.data || []).filter(
        req => req.vehicle_id === vehicle.id && (req.status === 'fulfilled' || req.status === 'archived')
      );
      setCompletedRequests(vehicleRequests);
    } catch (error) {
      console.error("Failed to fetch service history", error);
      setServiceHistory([]);
      setCompletedRequests([]);
    }
  }, [vehicle]);

  useEffect(() => {
    if (vehicle && open) {
      fetchServiceHistory();
      const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Fleet Admin";
      setServiceForm(prev => ({ 
        ...prev, 
        mileage_at_service: vehicle.mileage && vehicle.mileage > 0 ? vehicle.mileage.toLocaleString() : "",
        hours_at_service: vehicle.fuel_type === 'cng' && vehicle.engine_hours > 0 ? vehicle.engine_hours.toLocaleString() : "",
        performed_by: userName
      }));
      setActiveTab(defaultTab);
      setSelectedServiceType(null);
      setServiceImages([]);
    }
  }, [vehicle, open, fetchServiceHistory, defaultTab, currentUser]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setServiceImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setServiceImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRecordService = async () => {
    if (!selectedServiceType) {
      toast.error("Please select a service type");
      return;
    }
    
    const mileageValue = parseInt(serviceForm.mileage_at_service.replace(/,/g, '')) || 0;
    const isNoteOnly = selectedServiceType.id === "note";
    
    // For notes, mileage is optional. For other services, it's required.
    if (!isNoteOnly && mileageValue === 0) {
      toast.error("Please enter the current mileage");
      return;
    }
    
    // For notes, require a note to be entered
    if (isNoteOnly && !serviceForm.notes.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    const hoursValue = vehicle.fuel_type === 'cng' 
      ? parseInt(serviceForm.hours_at_service.replace(/,/g, '')) || 0 
      : null;
    
    if (!isNoteOnly && vehicle.fuel_type === 'cng' && hoursValue === 0) {
      toast.error("Please enter the current engine hours");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/service-records`, {
        vehicle_id: vehicle.id,
        service_type: selectedServiceType.name,
        service_date: new Date().toISOString(),
        mileage_at_service: mileageValue || null,
        hours_at_service: hoursValue,
        notes: serviceForm.notes,
        performed_by: serviceForm.performed_by,
        images: serviceImages,
      });
      toast.success(isNoteOnly ? "Note added successfully" : "Service recorded successfully");
      await fetchServiceHistory();
      onUpdate();
      setSelectedServiceType(null);
      setServiceForm(prev => ({ ...prev, notes: "", hours_at_service: "" }));
      setServiceImages([]);
    } catch (error) {
      toast.error("Failed to record service");
    }
    setLoading(false);
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      await axios.delete(`${API}/service-records/${recordId}`);
      toast.success("Record deleted");
      fetchServiceHistory();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const handleDeleteServiceRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this service request?")) return;
    try {
      await axios.delete(`${API}/repair-requests/${requestId}`);
      toast.success("Service request deleted");
      fetchServiceHistory();
    } catch (error) {
      toast.error("Failed to delete service request");
    }
  };

  const handleDeleteVehicle = async () => {
    if (!window.confirm("Are you sure you want to delete this vehicle? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicle.id}`);
      toast.success("Vehicle deleted");
      onUpdate();
      onClose();
    } catch (error) {
      toast.error("Failed to delete vehicle");
    }
  };

  const handleOutOfService = async () => {
    const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Fleet Admin";
    if (vehicle.is_out_of_service) {
      try {
        await axios.post(`${API}/vehicles/${vehicle.id}/return-to-service?performed_by=${encodeURIComponent(userName)}`);
        toast.success("Vehicle returned to service");
        onUpdate();
      } catch (error) {
        toast.error("Failed to update vehicle status");
      }
      return;
    }
    setShowOosDialog(true);
  };

  const confirmOutOfService = async () => {
    if (!outOfServiceCategory) {
      toast.error("Please select a category");
      return;
    }
    if (!outOfServiceReason.trim()) {
      toast.error("Please enter a reason for marking out of service");
      return;
    }
    try {
      const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Fleet Admin";
      await axios.post(`${API}/vehicles/${vehicle.id}/out-of-service?reason=${encodeURIComponent(outOfServiceReason)}&category=${encodeURIComponent(outOfServiceCategory)}&performed_by=${encodeURIComponent(userName)}`);
      toast.success("Vehicle marked as out of service");
      onUpdate();
      setShowOosDialog(false);
      setOutOfServiceReason("");
      setOutOfServiceCategory("");
    } catch (error) {
      toast.error("Failed to update vehicle status");
    }
  };

  const handleUpdateReason = async () => {
    try {
      await axios.put(`${API}/vehicles/${vehicle.id}`, { 
        out_of_service_reason: outOfServiceReason,
        out_of_service_category: outOfServiceCategory 
      });
      toast.success("Reason updated");
      onUpdate();
      setEditingReason(false);
    } catch (error) {
      toast.error("Failed to update reason");
    }
  };

  if (!vehicle) return null;

  const isServicePastDue = (lastServiceDate, lastServiceMileage, intervalName) => {
    if (!lastServiceDate) return true;
    
    const interval = serviceIntervals.find(i => 
      i.name.toLowerCase().includes(intervalName.toLowerCase()) ||
      intervalName.toLowerCase().includes(i.name.toLowerCase().split(' ')[0])
    );
    
    if (!interval) return false;
    
    const lastDate = new Date(lastServiceDate);
    const now = new Date();
    const daysSinceService = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
    const milesSinceService = (vehicle.mileage || 0) - (lastServiceMileage || 0);
    
    switch (interval.interval_unit) {
      case 'days': return daysSinceService > interval.interval_value;
      case 'months': return daysSinceService > (interval.interval_value * 30);
      case 'yearly': return daysSinceService > (interval.interval_value * 365);
      case 'miles': return milesSinceService > interval.interval_value;
      default: return false;
    }
  };

  const bitPastDue = isServicePastDue(vehicle.last_bit_inspection, vehicle.last_bit_mileage, "B.I.T");
  const dotPastDue = isServicePastDue(vehicle.last_dot_inspection, vehicle.last_dot_mileage, "D.O.T");
  
  const isCngOilPastDue = () => {
    if (vehicle.fuel_type !== 'cng') return false;
    if (!vehicle.last_oil_change) return true;
    const currentHours = vehicle.engine_hours || 0;
    const lastHours = vehicle.last_oil_hours || 0;
    return (currentHours - lastHours) > 1000;
  };
  
  const oilPastDue = vehicle.fuel_type === 'cng' 
    ? isCngOilPastDue() 
    : isServicePastDue(vehicle.last_oil_change, vehicle.last_oil_mileage, "Oil");

  const getIntervalDisplay = (intervalName) => {
    if (intervalName.toLowerCase().includes('oil') && vehicle.fuel_type === 'cng') {
      return "Every 1,000 hours";
    }
    const interval = serviceIntervals.find(i => 
      i.name.toLowerCase().includes(intervalName.toLowerCase()) ||
      intervalName.toLowerCase().includes(i.name.toLowerCase().split(' ')[0])
    );
    if (!interval) return "";
    const unitMap = { days: "days", months: "months", yearly: "year", miles: "mi", hours: "hours", gallons: "gal" };
    return `Every ${interval.interval_value.toLocaleString()} ${unitMap[interval.interval_unit] || interval.interval_unit}`;
  };

  const builtInServiceTypes = [
    { id: "note", name: "Add Note", icon: StickyNote, color: "#5856D6", bg: "rgba(88, 86, 214, 0.1)" },
    { id: "mileage_update", name: "Mileage Update", icon: Gauge, color: "#8E8E93", bg: "rgba(142, 142, 147, 0.1)" },
  ];

  const intervalServiceTypes = serviceIntervals.map(interval => ({
    id: `interval_${interval.id}`,
    name: interval.name,
    icon: Wrench,
    color: "#007AFF",
    bg: "rgba(0, 122, 255, 0.1)",
    intervalId: interval.id
  }));

  const allServiceTypes = [...intervalServiceTypes, ...builtInServiceTypes];

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent 
          className="w-screen h-screen max-w-none max-h-none m-0 rounded-none p-0 flex flex-col fixed inset-0 overflow-hidden"
          aria-describedby="vehicle-detail-description"
        >
          <DialogTitle className="sr-only">Vehicle Details - Unit {vehicle.unit_number}</DialogTitle>
          <span id="vehicle-detail-description" className="sr-only">
            View and manage vehicle details, record service, and view service history
          </span>
          
          {/* Header - Fixed */}
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-12 py-4 sm:py-6 border-b bg-white flex-shrink-0 pr-14 sm:pr-16">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-blue-50 rounded-xl flex items-center justify-center">
                <Truck className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-900">Unit {vehicle.unit_number}</h2>
                <p className="text-xs sm:text-sm text-slate-500">{vehicle.year} {vehicle.make} {vehicle.model}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="status-dot-container">
                <StatusDot status={bitPastDue ? "due" : "ok"} label="BIT" />
                <StatusDot status={dotPastDue ? "due" : "ok"} label="DOT" />
                <StatusDot status={oilPastDue ? "due" : "ok"} label="OIL" />
              </div>
            </div>
          </div>

          {/* Tabs - Takes remaining height */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <TabsList className="w-full justify-start rounded-none border-b bg-white px-4 sm:px-6 lg:px-12 h-auto flex-shrink-0 overflow-x-auto">
              <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent px-3 sm:px-6 py-3 text-sm sm:text-base whitespace-nowrap" data-testid="tab-details">Details</TabsTrigger>
              {canRecordService && (
                <TabsTrigger value="service" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent px-3 sm:px-6 py-3 text-sm sm:text-base whitespace-nowrap" data-testid="tab-service">Record Service</TabsTrigger>
              )}
              <TabsTrigger value="history" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent px-3 sm:px-6 py-3 text-sm sm:text-base whitespace-nowrap" data-testid="tab-history">History</TabsTrigger>
            </TabsList>

            {/* Tab Content Container - This is the scrollable area */}
            <div className="flex-1 overflow-hidden relative">
              {/* Details Tab */}
              <TabsContent value="details" className="m-0 absolute inset-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
              {vehicle.is_out_of_service && (
                  <div className="out-of-service-banner">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-red-600 font-semibold">
                        <AlertOctagon className="w-5 h-5" />OUT OF SERVICE
                        {vehicle.out_of_service_category && (
                          <span className={`ml-2 text-xs px-2 py-1 rounded-full font-medium ${
                            vehicle.out_of_service_category === 'safety_hold' ? 'bg-red-100 text-red-700' :
                            vehicle.out_of_service_category === 'breakdown' ? 'bg-orange-100 text-orange-700' :
                            vehicle.out_of_service_category === 'repair' ? 'bg-yellow-100 text-yellow-700' :
                            vehicle.out_of_service_category === 'planned_maintenance' ? 'bg-blue-100 text-blue-700' :
                            vehicle.out_of_service_category === 'body_shop' ? 'bg-purple-100 text-purple-700' :
                            vehicle.out_of_service_category === 'administrative_hold' ? 'bg-slate-100 text-slate-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {oosCategories.find(c => c.value === vehicle.out_of_service_category)?.label || vehicle.out_of_service_category}
                          </span>
                        )}
                      </div>
                      {vehicle.out_of_service_date && (
                        <div className="oos-duration-display" data-testid="oos-duration-display">
                          <Clock className="w-4 h-4" /><span>{getOutOfServiceDuration(vehicle.out_of_service_date)}</span>
                        </div>
                      )}
                    </div>
                    {editingReason ? (
                      <div className="space-y-2">
                        <Select value={outOfServiceCategory} onValueChange={setOutOfServiceCategory}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select category..." />
                          </SelectTrigger>
                          <SelectContent>
                            {oosCategories.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                          <Input value={outOfServiceReason} onChange={(e) => setOutOfServiceReason(e.target.value)} placeholder="Enter reason..." className="flex-1" />
                          <Button size="sm" onClick={handleUpdateReason}>Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingReason(false)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-600">{vehicle.out_of_service_reason || "No reason provided"}</p>
                        <Button size="sm" variant="ghost" onClick={() => { 
                          setOutOfServiceReason(vehicle.out_of_service_reason || ""); 
                          setOutOfServiceCategory(vehicle.out_of_service_category || "");
                          setEditingReason(true); 
                        }}>
                          <Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="info-card"><div className="info-card-label">VIN</div><div className="info-card-value break-all">{vehicle.vin}</div></div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="info-card"><div className="info-card-label">Mileage</div><div className="info-card-value">{(vehicle.mileage || 0).toLocaleString()} miles</div></div>
                    {vehicle.fuel_type === 'cng' ? (
                      <div className="info-card"><div className="info-card-label">Engine Hours</div><div className="info-card-value">{(vehicle.engine_hours || 0).toLocaleString()} hours</div></div>
                    ) : (
                      <div className="info-card"><div className="info-card-label">Vehicle Type</div><div className="info-card-value">{vehicle.vehicle_type.replace('_', ' ')}</div></div>
                    )}
                  </div>

                  {vehicle.fuel_type === 'cng' && (
                    <div className="info-card"><div className="info-card-label">Vehicle Type</div><div className="info-card-value">{vehicle.vehicle_type.replace('_', ' ')}</div></div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div className="info-card"><div className="info-card-label">Year</div><div className="info-card-value">{vehicle.year}</div></div>
                    <div className="info-card"><div className="info-card-label">Make</div><div className="info-card-value">{vehicle.make}</div></div>
                    <div className="info-card"><div className="info-card-label">Model</div><div className="info-card-value">{vehicle.model}</div></div>
                  </div>

                  {(vehicle.engine_year || vehicle.engine_make || vehicle.engine_model || vehicle.engine_serial) && (
                    <div className="mt-6">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Engine Info</h4>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="info-card"><div className="info-card-label">Engine Year</div><div className="info-card-value">{vehicle.engine_year || "-"}</div></div>
                        <div className="info-card"><div className="info-card-label">Engine Make</div><div className="info-card-value">{vehicle.engine_make || "-"}</div></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="info-card"><div className="info-card-label">Engine Model</div><div className="info-card-value">{vehicle.engine_model || "-"}</div></div>
                        <div className="info-card"><div className="info-card-label">Engine Serial #</div><div className="info-card-value">{vehicle.engine_serial || "-"}</div></div>
                      </div>
                    </div>
                  )}

                  {(vehicle.transmission_make || vehicle.transmission_model || vehicle.transmission_type) && (
                    <div className="mt-6">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Transmission Info</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="info-card"><div className="info-card-label">Make</div><div className="info-card-value">{vehicle.transmission_make || "-"}</div></div>
                        <div className="info-card"><div className="info-card-label">Model</div><div className="info-card-value">{vehicle.transmission_model || "-"}</div></div>
                        <div className="info-card"><div className="info-card-label">Type</div><div className="info-card-value capitalize">{vehicle.transmission_type || "-"}</div></div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Fuel Configuration</h4>
                    <div className="fuel-config-section">
                      <div className={`fuel-card ${vehicle.fuel_type}`}>
                        <div className="info-card-label">Fuel Type</div>
                        <div className="info-card-value" style={{ color: vehicle.fuel_type === "diesel" ? "#34C759" : "#007AFF" }}>
                          {vehicle.fuel_type === "diesel" ? "Diesel" : "CNG"}
                        </div>
                      </div>
                      <div className="fuel-card def">
                        <div className="info-card-label">DEF System</div>
                        <div className="info-card-value" style={{ color: "#007AFF" }}>{vehicle.def_equipped ? "Equipped" : "Not Equipped"}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Last Services</h4>
                    <div className="last-services-grid">
                      <div className={`service-status-card ${bitPastDue ? 'past-due' : ''}`}>
                        <div className="service-status-header"><div className="service-status-dot" style={{ backgroundColor: bitPastDue ? "#FF3B30" : "#34C759" }} /><span className="service-status-label">B.I.T. Inspection</span></div>
                        <div className="service-status-value">{vehicle.last_bit_inspection ? new Date(vehicle.last_bit_inspection).toLocaleDateString() : "Never"}</div>
                        {vehicle.last_bit_mileage > 0 && <div className="service-status-mileage">@ {vehicle.last_bit_mileage.toLocaleString()} mi</div>}
                        <div className="service-status-interval">{getIntervalDisplay("B.I.T")}</div>
                      </div>
                      <div className={`service-status-card ${dotPastDue ? 'past-due' : ''}`}>
                        <div className="service-status-header"><div className="service-status-dot" style={{ backgroundColor: dotPastDue ? "#FF3B30" : "#34C759" }} /><span className="service-status-label">D.O.T. Inspection</span></div>
                        <div className="service-status-value">{vehicle.last_dot_inspection ? new Date(vehicle.last_dot_inspection).toLocaleDateString() : "Never"}</div>
                        {vehicle.last_dot_mileage > 0 && <div className="service-status-mileage">@ {vehicle.last_dot_mileage.toLocaleString()} mi</div>}
                        <div className="service-status-interval">{getIntervalDisplay("D.O.T")}</div>
                      </div>
                      <div className={`service-status-card ${oilPastDue ? 'past-due' : ''}`}>
                        <div className="service-status-header"><div className="service-status-dot" style={{ backgroundColor: oilPastDue ? "#FF3B30" : "#34C759" }} /><span className="service-status-label">Oil Change</span></div>
                        <div className="service-status-value">{vehicle.last_oil_change ? new Date(vehicle.last_oil_change).toLocaleDateString() : "Never"}</div>
                        {vehicle.fuel_type === 'cng' ? (vehicle.last_oil_hours > 0 && <div className="service-status-mileage">@ {vehicle.last_oil_hours.toLocaleString()} hrs</div>) : (vehicle.last_oil_mileage > 0 && <div className="service-status-mileage">@ {vehicle.last_oil_mileage.toLocaleString()} mi</div>)}
                        <div className="service-status-interval">{getIntervalDisplay("Oil")}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {canEdit && (
                    <Button variant="outline" className="w-full justify-center gap-2" onClick={() => setShowEditModal(true)} data-testid="btn-edit-vehicle"><Pencil className="w-4 h-4" /> Edit Vehicle</Button>
                  )}
                  {canDelete && (
                    <Button variant="destructive" className="w-full justify-center" onClick={handleDeleteVehicle} data-testid="btn-delete-vehicle">Delete Vehicle</Button>
                  )}
                  {(canMarkOOS || canReturnToService) && (
                    <Button 
                      variant="outline" 
                      className={`w-full justify-center gap-2 ${vehicle.is_out_of_service ? 'border-green-500 text-green-600 hover:bg-green-50' : 'border-red-500 text-red-600 hover:bg-red-50'}`} 
                      onClick={handleOutOfService} 
                      data-testid="btn-out-of-service"
                      disabled={vehicle.is_out_of_service ? !canReturnToService : !canMarkOOS}
                    >
                      {vehicle.is_out_of_service ? (<><CheckCircle className="w-4 h-4" /> Return to Service</>) : (<><AlertOctagon className="w-4 h-4" /> Mark Out of Service</>)}
                    </Button>
                  )}
                </div>
                {/* Extra space for safe area on mobile */}
                <div className="h-20 sm:h-8"></div>
                </div>
              </TabsContent>

              {/* Record Service Tab */}
              <TabsContent value="service" className="m-0 absolute inset-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 space-y-6">
                  <div>
                    <h4 className="font-medium text-slate-900 mb-4">Select Service Type</h4>
                    <div className="service-type-grid">
                      {allServiceTypes.map((serviceType) => {
                        const Icon = serviceType.icon;
                        return (
                          <button key={serviceType.id} onClick={() => setSelectedServiceType(serviceType)} className={`service-type-card p-4 rounded-lg border text-left ${selectedServiceType?.id === serviceType.id ? "selected" : "border-slate-200"}`} data-testid={`service-type-${serviceType.id}`}>
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: serviceType.bg }}><Icon className="w-5 h-5" style={{ color: serviceType.color }} /></div>
                            <div className="text-sm font-medium text-slate-700">{serviceType.name}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {(!selectedServiceType || selectedServiceType.id !== "note") && (
                    <div className={`grid ${vehicle.fuel_type === 'cng' ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                      <div>
                        <Label htmlFor="service_mileage">Current Mileage *</Label>
                        <Input id="service_mileage" type="text" inputMode="numeric" data-testid="input-service-mileage" value={serviceForm.mileage_at_service} onChange={(e) => { const rawValue = e.target.value.replace(/[^0-9]/g, ''); setServiceForm({ ...serviceForm, mileage_at_service: rawValue === '' ? '' : parseInt(rawValue).toLocaleString() }); }} className="text-lg" placeholder="Enter mileage" />
                      </div>
                      {vehicle.fuel_type === 'cng' && (
                        <div>
                          <Label htmlFor="service_hours">Current Hours *</Label>
                          <Input id="service_hours" type="text" inputMode="numeric" data-testid="input-service-hours" value={serviceForm.hours_at_service} onChange={(e) => { const rawValue = e.target.value.replace(/[^0-9]/g, ''); setServiceForm({ ...serviceForm, hours_at_service: rawValue === '' ? '' : parseInt(rawValue).toLocaleString() }); }} className="text-lg" placeholder="Enter engine hours" />
                        </div>
                      )}
                    </div>
                  )}

                  {selectedServiceType?.id === "note" && (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="service_mileage">Current Mileage (optional)</Label>
                        <Input id="service_mileage" type="text" inputMode="numeric" data-testid="input-service-mileage" value={serviceForm.mileage_at_service} onChange={(e) => { const rawValue = e.target.value.replace(/[^0-9]/g, ''); setServiceForm({ ...serviceForm, mileage_at_service: rawValue === '' ? '' : parseInt(rawValue).toLocaleString() }); }} className="text-lg" placeholder="Enter mileage (optional)" />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="mb-3 block">Photos</Label>
                    <div className="space-y-3">
                      {serviceImages.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                          {serviceImages.map((img, index) => (
                            <div key={index} className="relative group aspect-square">
                              <img src={img} alt={`Service photo ${index + 1}`} className="w-full h-full object-cover rounded-lg border border-slate-200" />
                              <button onClick={() => removeImage(index)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md" type="button"><X className="w-4 h-4" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="relative">
                        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="input-service-photos" />
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                          <Camera className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                          <p className="text-sm font-medium text-slate-600">{serviceImages.length > 0 ? "Add More Photos" : "Upload Photos"}</p>
                          <p className="text-xs text-slate-400 mt-1">Click or tap to select images</p>
                        </div>
                      </div>
                      {serviceImages.length > 0 && <p className="text-xs text-slate-500">{serviceImages.length} photo{serviceImages.length !== 1 ? 's' : ''} attached</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="service_notes">
                      {selectedServiceType?.id === "note" ? "Note *" : "Notes"}
                    </Label>
                    <Textarea 
                      id="service_notes" 
                      data-testid="input-service-notes" 
                      value={serviceForm.notes} 
                      onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })} 
                      placeholder={selectedServiceType?.id === "note" ? "Enter your note..." : "Add any notes about this service..."} 
                      rows={4} 
                      className="resize-none" 
                    />
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-1"><User className="w-4 h-4" />Will be recorded by: <span className="font-medium">{serviceForm.performed_by}</span></div>
                    <div className="flex items-center gap-2 text-sm text-slate-600"><Clock className="w-4 h-4" />Date/Time: <span className="font-medium">{new Date().toLocaleString()}</span></div>
                  </div>

                  <Button 
                    className="w-full" 
                    onClick={handleRecordService} 
                    disabled={loading || !selectedServiceType} 
                    data-testid="btn-record-service"
                  >
                    {loading ? "Recording..." : selectedServiceType?.id === "note" ? "Add Note" : "Record Service"}
                  </Button>
                  {/* Extra space for safe area on mobile */}
                  <div className="h-24 sm:h-8"></div>
                </div>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history" className="m-0 absolute inset-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
                {serviceHistory.length === 0 && completedRequests.length === 0 ? (
                  <div className="text-center py-12 text-slate-500"><Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No service history yet</p></div>
                ) : (
                  <div className="space-y-3">
                    {/* Combine and sort all history items by date */}
                    {[
                      ...serviceHistory.map(record => ({ 
                        ...record, 
                        type: 'service', 
                        date: record.service_date ? new Date(record.service_date) : new Date(0)
                      })),
                      ...completedRequests.map(request => ({ 
                        ...request, 
                        type: 'request', 
                        date: new Date(request.fulfilled_at || request.created_at || 0)
                      }))
                    ]
                      .filter(item => !isNaN(item.date.getTime()))
                      .sort((a, b) => b.date - a.date)
                      .map((item) => {
                        if (item.type === 'service') {
                          const record = item;
                          const builtInConfig = SERVICE_TYPE_CONFIG[record.service_type];
                          const config = builtInConfig || { label: record.service_type, icon: Wrench, color: "#007AFF", bg: "rgba(0, 122, 255, 0.1)" };
                          const Icon = config.icon;
                          return (
                            <div key={`service-${record.id}`} className="history-item" data-testid={`history-item-${record.id}`}>
                              <div className="history-icon" style={{ backgroundColor: config.bg }}><Icon className="w-5 h-5" style={{ color: config.color }} /></div>
                              <div className="history-content">
                                <div className="history-title">{builtInConfig ? config.label : record.service_type || 'Unknown Service'}</div>
                                <div className="history-meta">{record.service_date ? new Date(record.service_date).toLocaleString() : 'Unknown date'}</div>
                                <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                                  <div className="flex items-center gap-1"><User className="w-3 h-3" />{record.performed_by || 'Unknown'}</div>
                                  {record.mileage_at_service > 0 && <div className="flex items-center gap-1"><Gauge className="w-3 h-3" />{Number(record.mileage_at_service).toLocaleString()} mi</div>}
                                </div>
                                {record.notes && <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">{record.notes}</p>}
                                {record.images && record.images.length > 0 && (
                                  <div className="mt-3">
                                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-2"><ImageIcon className="w-3 h-3" />{record.images.length} photo{record.images.length !== 1 ? 's' : ''}</div>
                                    <div className="flex gap-2 overflow-x-auto pb-2">
                                      {record.images.map((img, imgIndex) => (<img key={imgIndex} src={img} alt={`Service photo ${imgIndex + 1}`} className="w-20 h-20 object-cover rounded-lg border border-slate-200 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setLightboxImage(img)} />))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-2"><button onClick={() => handleDeleteRecord(record.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`btn-delete-record-${record.id}`}><Trash2 className="w-4 h-4" /></button></div>
                            </div>
                          );
                        } else {
                          // Service Request item
                          const request = item;
                          const priorityColors = {
                            high: { color: "#dc2626", bg: "rgba(220, 38, 38, 0.1)" },
                            medium: { color: "#ca8a04", bg: "rgba(202, 138, 4, 0.1)" },
                            low: { color: "#16a34a", bg: "rgba(22, 163, 74, 0.1)" }
                          };
                          const pColor = priorityColors[request.priority] || priorityColors.medium;
                          return (
                            <div key={`request-${request.id}`} className="history-item" data-testid={`history-request-${request.id}`}>
                              <div className="history-icon" style={{ backgroundColor: "rgba(34, 197, 94, 0.1)" }}>
                                <ClipboardList className="w-5 h-5" style={{ color: "#22c55e" }} />
                              </div>
                              <div className="history-content">
                                <div className="flex items-center gap-2">
                                  <span className="history-title">Service Request: {request.category}</span>
                                  {request.priority && (
                                    <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ backgroundColor: pColor.bg, color: pColor.color }}>
                                      {request.priority.toUpperCase()}
                                    </span>
                                  )}
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                    {request.status === 'archived' ? 'ARCHIVED' : 'COMPLETED'}
                                  </span>
                                </div>
                                <div className="history-meta">
                                  {request.fulfilled_at ? `Completed ${new Date(request.fulfilled_at).toLocaleString()}` : `Submitted ${new Date(request.created_at).toLocaleString()}`}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                                  <div className="flex items-center gap-1"><User className="w-3 h-3" />{request.requested_by}</div>
                                  {request.mileage && <div className="flex items-center gap-1"><Gauge className="w-3 h-3" />{Number(request.mileage).toLocaleString()} mi</div>}
                                </div>
                                {request.notes && <p className="text-sm text-slate-600 mt-2 bg-green-50 p-2 rounded">{request.notes}</p>}
                                {request.images && request.images.length > 0 && (
                                  <div className="mt-3">
                                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-2"><ImageIcon className="w-3 h-3" />{request.images.length} photo{request.images.length !== 1 ? 's' : ''}</div>
                                    <div className="flex gap-2 overflow-x-auto pb-2">
                                      {request.images.map((img, imgIndex) => (<img key={imgIndex} src={img} alt={`Request photo ${imgIndex + 1}`} className="w-20 h-20 object-cover rounded-lg border border-slate-200 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setLightboxImage(img)} />))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <button onClick={() => handleDeleteServiceRequest(request.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`btn-delete-request-${request.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        }
                      })}
                  </div>
                )}
                {/* Extra space for safe area on mobile */}
                <div className="h-24 sm:h-8"></div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Out of Service Reason Dialog */}
      <Dialog open={showOosDialog} onOpenChange={setShowOosDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><AlertOctagon className="w-5 h-5" />Mark Out of Service</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">Please categorize and provide a reason for marking <strong>Unit {vehicle?.unit_number}</strong> as out of service.</p>
            <div>
              <Label htmlFor="oos_category">Category *</Label>
              <Select value={outOfServiceCategory} onValueChange={setOutOfServiceCategory}>
                <SelectTrigger data-testid="select-oos-category" className="mt-1">
                  <SelectValue placeholder="Select a category..." />
                </SelectTrigger>
                <SelectContent>
                  {oosCategories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label htmlFor="oos_reason">Reason / Notes *</Label><Textarea id="oos_reason" data-testid="input-oos-reason" value={outOfServiceReason} onChange={(e) => setOutOfServiceReason(e.target.value)} placeholder="Enter details about why this unit is out of service..." rows={3} className="mt-1" /></div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => { setShowOosDialog(false); setOutOfServiceReason(""); setOutOfServiceCategory(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={confirmOutOfService} disabled={!outOfServiceReason.trim() || !outOfServiceCategory} data-testid="btn-confirm-oos"><AlertOctagon className="w-4 h-4 mr-2" />Mark Out of Service</Button>
          </div>
        </DialogContent>
      </Dialog>

      <VehicleFormModal open={showEditModal} onClose={() => setShowEditModal(false)} vehicle={vehicle} onSave={() => { onUpdate(); setShowEditModal(false); }} />
    </>
  );
};
